// NEW_FILE_JUSTIFIED: Step 4 of claim wizard — bank details, extracted to stay under 200 lines
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import type { IClaimFormState } from '@/hooks/useClaimForm';

// LOCAL_TYPE_JUSTIFIED: step-internal props, not reused outside wizard
interface IClaimWizardStepPaymentProps {
  state: IClaimFormState;
  onField: (field: keyof IClaimFormState, value: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
}

export function ClaimWizardStepPayment({
  state,
  onField,
  onNext,
  onPrev,
  onSaveDraft,
  isSavingDraft,
}: IClaimWizardStepPaymentProps) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">Provide bank details for reimbursement.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Reimburse To</Label>
          <Select value={state.reimburseTarget} onValueChange={v => onField('reimburseTarget', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="insured">Insured (me)</SelectItem>
              <SelectItem value="provider">Provider</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Bank Name</Label>
          <Input
            value={state.bankName}
            onChange={e => onField('bankName', e.target.value)}
            placeholder="e.g. HSBC"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Account Holder Name</Label>
          <Input
            value={state.bankAccountHolder}
            onChange={e => onField('bankAccountHolder', e.target.value)}
            placeholder="Full name as on account"
          />
        </div>
        <div className="space-y-1.5">
          <Label>IBAN</Label>
          <Input
            value={state.bankIban}
            onChange={e => onField('bankIban', e.target.value)}
            placeholder="GB29 NWBK 6016 1331 9268 19"
          />
        </div>
        <div className="space-y-1.5">
          <Label>SWIFT / BIC</Label>
          <Input
            value={state.bankSwift}
            onChange={e => onField('bankSwift', e.target.value)}
            placeholder="e.g. HBUKGB4B"
          />
        </div>
      </div>
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onPrev}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex items-center gap-2">
          {onSaveDraft && (
            <Button variant="outline" onClick={onSaveDraft} disabled={isSavingDraft}>
              <Save className="mr-2 h-4 w-4" /> Save Draft
            </Button>
          )}
          <Button onClick={onNext} data-testid="claim-wizard-next">
            Review <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
